import { createContext, useCallback, useContext, useEffect, useReducer } from "react";
import { clientApi, tokens } from "../lib/api";

const SessionContext = createContext(null);

const initialState = {
  customer: null,
  loading: true,
  usingGenericPwd: false,
  mustChangePassword: false,
};

function reducer(state, action) {
  switch (action.type) {
    case "set_customer":
      return { ...state, customer: action.customer };
    case "set_loading":
      return { ...state, loading: action.loading };
    case "set_generic":
      return { ...state, usingGenericPwd: action.value };
    case "set_must_change":
      return { ...state, mustChangePassword: action.value };
    case "after_bootstrap":
      return {
        ...state,
        loading: false,
        usingGenericPwd: action.generic,
        mustChangePassword: action.mustChange,
      };
    case "after_login":
      return {
        customer: action.customer,
        loading: false,
        usingGenericPwd: !!action.generic,
        mustChangePassword: !!action.mustChange,
      };
    case "logout":
      return {
        customer: null,
        loading: false,
        usingGenericPwd: false,
        mustChangePassword: false,
      };
    default:
      return state;
  }
}

export const SessionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const timer = setTimeout(() => {
      const t = tokens.getClient();
      const genericFromStorage = sessionStorage.getItem("cm_using_generic") === "1";
      const mustChangeFromStorage = sessionStorage.getItem("cm_must_change_pwd") === "1";
      if (!t) {
        dispatch({
          type: "after_bootstrap",
          generic: genericFromStorage,
          mustChange: mustChangeFromStorage,
        });
        return;
      }
      clientApi.get("/portal/me")
        .then((r) => dispatch({ type: "set_customer", customer: r.data }))
        .catch(() => tokens.clearClient())
        .finally(() => dispatch({
          type: "after_bootstrap",
          generic: genericFromStorage,
          mustChange: mustChangeFromStorage,
        }));
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const _persistAndDispatch = (data) => {
    tokens.setClient(data.access_token);
    sessionStorage.setItem("cm_using_generic", data.using_generic_password ? "1" : "0");
    sessionStorage.setItem("cm_must_change_pwd", data.must_change_password ? "1" : "0");
    dispatch({
      type: "after_login",
      customer: data.customer,
      generic: data.using_generic_password,
      mustChange: data.must_change_password,
    });
  };

  const loginNit = useCallback((nit, password) => {
    return clientApi.post("/portal/login-nit", { nit, password }).then(({ data }) => {
      if (data.multi_sede) {
        return { multi_sede: true, nit: data.nit, razon_social: data.razon_social, sedes: data.sedes };
      }
      _persistAndDispatch(data);
      return data.customer;
    });
  }, []);

  const selectSede = useCallback((nit, clienteId, password) => {
    const body = { nit: nit, cliente_id: clienteId, password: password };
    return clientApi.post("/portal/select-sede", body).then(({ data }) => {
      _persistAndDispatch(data);
      return data.customer;
    });
  }, []);

  const markPasswordPersonal = useCallback(() => {
    sessionStorage.setItem("cm_using_generic", "0");
    sessionStorage.setItem("cm_must_change_pwd", "0");
    dispatch({ type: "set_generic", value: false });
    dispatch({ type: "set_must_change", value: false });
  }, []);

  const logout = useCallback(() => {
    tokens.clearClient();
    sessionStorage.removeItem("cm_using_generic");
    sessionStorage.removeItem("cm_must_change_pwd");
    dispatch({ type: "logout" });
  }, []);

  return (
    <SessionContext.Provider value={{
      customer: state.customer,
      loading: state.loading,
      usingGenericPwd: state.usingGenericPwd,
      mustChangePassword: state.mustChangePassword,
      loginNit,
      selectSede,
      logout,
      markPasswordPersonal,
    }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
